/**
 * Shared validation logic for assets (Australian Standards).
 * Centralizes thresholds like RCD Trip Time so we don't repeat them in Modals and Cards.
 */

export const RCD_THRESHOLDS = { MAX_TRIP_TIME: 300 };

/** Auto-generated defect string for an RCD that failed to trip under test. */
export const DID_NOT_TRIP_DEFECT = 'Did Not Trip';

/** Defect/note text for a push button that did not operate mechanically. */
export const PUSH_BUTTON_FAILURE_DEFECT = 'Push Button Mechanical Failure';

/**
 * Build the secondary "Push Button" test row that accompanies an RCD's
 * "Operating Time" result.
 *
 * The push-button check is a separate test with its own compliance clock, so it
 * has to be persisted as its own `test_results` row. Folding it into the
 * Operating Time row's result/defect text means the report cannot report that
 * test on its own (it renders an em dash) and the `push_button` due date never
 * advances, so the RCD silently goes overdue on that interval.
 *
 * Mirrors the inline save path (see useTestSession.handleInlineRcdSave), which
 * already stages this row alongside the primary result.
 *
 * The failure reason is written to `defectsFound`, never `notes` — severity
 * resolution reads only `defectsFound` (see Brief 43), and `notes` stays the
 * technician's free-text field.
 *
 * Returns `null` when the push-button result is unset, so no row is written at
 * all. An unanswered test must never be recorded as either a pass or a fail —
 * a fail would be just as untrue as a pass, and a pass would advance a
 * twelve-month compliance date for a test nobody performed. Callers must block
 * the save instead; this is the defensive backstop, not the primary guard.
 *
 * @param {{ assetId: number|string, testDate: string, pushButtonStatus: string, sessionId?: string|null }} args
 * @returns {object|null}
 */
export function buildPushButtonTestPayload({ assetId, testDate, pushButtonStatus, sessionId = null }) {
  if (pushButtonStatus !== 'pass' && pushButtonStatus !== 'fail') return null;

  const passed = pushButtonStatus === 'pass';
  const payload = {
    assetId,
    testDate,
    testType: 'Push Button',
    result: passed ? 'pass' : 'fail',
  };
  if (sessionId != null && String(sessionId).trim() !== '') {
    payload.sessionId = String(sessionId).trim();
  }
  if (!passed) {
    payload.defectsFound = PUSH_BUTTON_FAILURE_DEFECT;
  }
  return payload;
}

/**
 * Validate an RCD result.
 *
 * @param {string|number} tripTimeMs - measured trip time in ms (ignored when didNotTrip)
 * @param {string} pushButtonStatus - 'pass' | 'fail'
 * @param {{ didNotTrip?: boolean }} [options]
 *   didNotTrip: the RCD never operated under test. No trip-time number is
 *   recorded (tripTimeMs comes back null) so nothing fabricated reaches the
 *   report; the result is always 'fail'.
 */
export function validateRcdResult(tripTimeMs, pushButtonStatus, options = {}) {
  const { didNotTrip = false } = options;

  // "Did not trip" is a valid, recordable outcome with no measurement. Follow
  // the same autoDefect join pattern as below so downstream severity rules can
  // key off these strings.
  if (didNotTrip) {
    const parts = [];
    if (pushButtonStatus !== 'pass') parts.push(PUSH_BUTTON_FAILURE_DEFECT);
    parts.push(DID_NOT_TRIP_DEFECT);
    return {
      valid: true,
      result: 'fail',
      tripTimeMs: null,
      autoDefect: parts.join(', '),
    };
  }

  const tripStr = (tripTimeMs ?? '').toString().trim();

  if (!tripStr) {
    return { valid: false, error: 'Please enter Trip Time (ms)' };
  }

  const ms = parseInt(tripStr, 10);
  if (Number.isNaN(ms) || ms < 0) {
    return { valid: false, error: 'Please enter a valid Trip Time (ms)' };
  }

  const isPass = pushButtonStatus === 'pass' && ms <= RCD_THRESHOLDS.MAX_TRIP_TIME;

  let autoDefect = null;
  if (!isPass) {
    const parts = [];
    if (pushButtonStatus !== 'pass') parts.push(PUSH_BUTTON_FAILURE_DEFECT);
    if (ms > RCD_THRESHOLDS.MAX_TRIP_TIME) parts.push(`Trip Time Exceeded (${ms}ms)`);
    autoDefect = parts.join(', ') || 'Auto-Fail: General RCD Failure';
  }

  return {
    valid: true,
    result: isPass ? 'pass' : 'fail',
    tripTimeMs: ms,
    autoDefect
  };
}
